/**
 * PATH: apps/quote/src/components/builder/scope-sheet-grid/ColumnHeaders.tsx
 * PURPOSE: Two-row banded column headers (Excel-style) for the scope sheet grid.
 */

import { cn } from '@/lib/utils'
import type { ColumnBand, Column } from './ScopeSheetGridTypes'
import ColumnHeaderMenu from './ColumnHeaderMenu'

interface ColumnHeadersProps {
  columnBands: ColumnBand[]
  allColumns: Column[]
  gridStyle: React.CSSProperties
  /**
   * Currently visible optional columns (mirrors `quote.config.visible_columns`).
   * Forwarded to the per-header dropdown menu so toggles reflect current state.
   */
  visibleColumnIds?: string[]
  /**
   * Persists a partial patch into `quote.config`. Called by the inline column
   * menu when the user toggles visibility from the header instead of going to
   * Setup. Optional — when omitted, the menu chevron is suppressed.
   */
  onUpdateConfig?: (patch: Record<string, unknown>) => void
  /** Optional handler for "Add a custom column…" menu item. */
  onOpenSetup?: () => void
}

export default function ColumnHeaders({
  columnBands,
  allColumns,
  gridStyle,
  visibleColumnIds,
  onUpdateConfig,
  onOpenSetup,
}: ColumnHeadersProps) {
  return (
    <div className="sticky top-0 z-20 flex-shrink-0">
      {/* Row A: Band headers */}
      <div className="flex h-6 bg-slate-700 dark:bg-slate-950 border-b border-slate-600 dark:border-slate-700">
        {columnBands.map((band) => {
          const totalWidth = band.columns.reduce((sum, col) => {
            if (col.hidden) return sum
            if (col.width === 'flex') return sum
            return sum + (col.width as number)
          }, 0)
          const hasFlex = band.columns.some((c) => c.width === 'flex' && !c.hidden)
          const hasAnyVisible = band.columns.some((c) => !c.hidden)
          if (!hasAnyVisible) return null

          return (
            <div
              key={band.id}
              className={cn(
                'flex items-center justify-center text-[10px] font-semibold uppercase tracking-wider border-r border-slate-600 dark:border-slate-700 last:border-r-0',
                band.textClass
              )}
              style={{
                width: hasFlex ? undefined : totalWidth,
                flex: hasFlex ? 1 : undefined,
                minWidth: hasFlex ? 0 : undefined,
              }}
            >
              {band.label}
            </div>
          )
        })}
      </div>

      {/* Row B: Column headers */}
      <div className="grid h-7 bg-slate-100 dark:bg-slate-800 border-b border-slate-300 dark:border-slate-600" style={gridStyle}>
        {columnBands.flatMap((band) =>
          band.columns.map((col) => (
            <div
              key={col.id}
              className={cn(
                'relative flex items-center px-1.5 text-[10px] font-medium border-r border-slate-200 dark:border-slate-700 last:border-r-0',
                col.hidden && 'px-0 border-r-0 overflow-hidden',
                band.bgClass,
                col.computed ? 'text-slate-400 dark:text-slate-500' : band.textClass,
                col.align === 'right' && 'justify-end',
                col.align === 'center' && 'justify-center'
              )}
              title={col.label}
            >
              {!col.hidden && (
                <span className="truncate">{col.label}</span>
              )}
              {/* Inline column menu — only rendered for visible columns and only
                  when the parent has wired `onUpdateConfig`. Custom columns are
                  excluded for now (they're managed in Setup → Custom Columns). */}
              {!col.hidden && !col.custom && onUpdateConfig && (
                <ColumnHeaderMenu
                  column={col}
                  visibleColumnIds={visibleColumnIds ?? []}
                  onUpdateConfig={onUpdateConfig}
                  onOpenSetup={onOpenSetup}
                />
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
